// skipcq: JS-W1028
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import statesTopology from 'us-atlas/states-10m.json';
import {
  getMapMetricRange,
  getMapMetricValue,
  normalizeValueForColorScale,
  resolveStateCodeFromGeographyName,
} from '../../utils/beerActivityByStateReport';

const MAP_WIDTH = 800;
const MAP_HEIGHT = 500;
const EMPTY_FILL = '#e8e4dc';
const HIGH_FILL_RGB = { red: 61, green: 92, blue: 74 };
const LOW_FILL_RGB = { red: 232, green: 228, blue: 220 };

/**
 * @param {number} intensity 0–1
 * @returns {string}
 */
function blendMapFillColor(intensity) {
  const ratio = Math.min(1, Math.max(0, intensity));
  const red = Math.round(LOW_FILL_RGB.red + (HIGH_FILL_RGB.red - LOW_FILL_RGB.red) * ratio);
  const green = Math.round(LOW_FILL_RGB.green + (HIGH_FILL_RGB.green - LOW_FILL_RGB.green) * ratio);
  const blue = Math.round(LOW_FILL_RGB.blue + (HIGH_FILL_RGB.blue - LOW_FILL_RGB.blue) * ratio);
  return `rgb(${red}, ${green}, ${blue})`;
}

/**
 * @param {number} metricValue
 * @param {number} colorIntensity
 * @returns {string}
 */
function resolveGeographyHoverFill(metricValue, colorIntensity) {
  if (metricValue <= 0) {
    return EMPTY_FILL;
  }
  return blendMapFillColor(Math.min(1, colorIntensity + 0.15));
}

function BeerActivityByStateMap({ byState, metricKey }) {
  const metricRange = getMapMetricRange(byState, metricKey);

  return (
    <Box sx={{ width: '100%', maxWidth: MAP_WIDTH, margin: '0 auto' }}>
      <ComposableMap
        projection="geoAlbersUsa"
        width={MAP_WIDTH}
        height={MAP_HEIGHT}
        style={{ width: '100%', height: 'auto' }}
      >
        <Geographies geography={statesTopology}>
          {({ geographies }) =>
            geographies.map((geography) => {
              const stateCode = resolveStateCodeFromGeographyName(geography.properties?.name);
              const metricValue = stateCode
                ? getMapMetricValue(byState, stateCode, metricKey)
                : 0;
              const colorIntensity = normalizeValueForColorScale(
                metricValue,
                metricRange.min,
                metricRange.max,
              );
              const fillColor =
                metricValue > 0 ? blendMapFillColor(colorIntensity) : EMPTY_FILL;
              const hoverFill = resolveGeographyHoverFill(metricValue, colorIntensity);

              return (
                <Geography
                  key={geography.rsmKey}
                  geography={geography}
                  fill={fillColor}
                  stroke="#5c5348"
                  strokeWidth={0.35}
                  style={{
                    default: { outline: 'none' },
                    hover: { outline: 'none', fill: hoverFill },
                    pressed: { outline: 'none' },
                  }}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
        States with no activity in the window are shown in gray.
      </Typography>
    </Box>
  );
}

BeerActivityByStateMap.propTypes = {
  byState: PropTypes.object.isRequired,
  metricKey: PropTypes.string.isRequired,
};

export default BeerActivityByStateMap;
